import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-of-service-page">
      {/* Hero Section */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-[#F1F5F8]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#163A59] font-medium mb-6 hover:gap-3 transition-all"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Terms of Use
            </h1>
            <p className="text-slate-600">Last updated: February 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-slate max-w-none">
            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              By accessing and using the website of the Geneva Institute of Humanitarian & Development Studies (GIHDS) at www.gihds.org, or by enrolling in any of our programs or courses, you agree to be bound by these Terms of Use. If you do not agree, please discontinue use of our website and services.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">2. Description of Services</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              The Geneva Institute of Humanitarian & Development Studies (GIHDS) provides online academic and professional education, including the following programs:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li><strong>Master of Humanitarian Action & Development</strong> (1 year)</li>
              <li><strong>Master of Development Project & Program Management</strong> (9 months)</li>
              <li><strong>Doctor of Humanitarian Action & Development</strong> (3 years)</li>
              <li><strong>AI in Humanitarian Action & Development</strong> (4-week professional short course)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Services include access to course content, the online Learning Management System (LMS), faculty guidance, and, upon successful completion, the corresponding academic document as described on each program page.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">3. Eligibility</h2>
            <p className="text-slate-600 leading-relaxed mb-4">To enroll, applicants must meet the following requirements:</p>
            <p className="text-slate-600 leading-relaxed mb-2"><strong>Master of Humanitarian Action & Development / Master of Development Project & Program Management:</strong></p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Hold a bachelor's degree or equivalent qualification</li>
              <li>Be proficient in English</li>
              <li>Have access to a reliable internet connection</li>
              <li>Commit to completing the full program (9 months or 1 year, depending on program)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-2"><strong>Doctor of Humanitarian Action & Development:</strong></p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Hold a master's degree in a relevant field</li>
              <li>Have professional experience in the humanitarian or development sector</li>
              <li>Be proficient in English</li>
              <li>Have access to a reliable internet connection</li>
              <li>Commit to completing the 3-year research program</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-2"><strong>AI in Humanitarian Action & Development (Short Course):</strong></p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Open to professionals, students, and practitioners in humanitarian, development, NGO, public-sector, or social-impact work</li>
              <li>No prior technical or AI experience required</li>
              <li>Proficiency in English and reliable internet access</li>
            </ul>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">4. Program Fees</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Full program fees are as follows:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Master of Humanitarian Action & Development:</strong> USD 2,000 (total)</li>
              <li><strong>Master of Development Project & Program Management:</strong> USD 3,500 (total)</li>
              <li><strong>Doctor of Humanitarian Action & Development:</strong> USD 10,800 (total; USD 3,600 per year)</li>
              <li><strong>AI in Humanitarian Action & Development:</strong> USD 99 (Standard) / USD 59 (Current GIHDS students)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Eligible learners from Africa and Asia may apply for our institutional <strong>90% scholarship</strong> on degree programs. Scholarship eligibility is determined solely by GIHDS. Please review our <Link to="/refund-policy" className="text-[#163A59] underline">Refund Policy</Link> before making any payment.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">5. Scholarship Terms</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              The 90% GIHDS Global Scholarship Initiative is available to eligible applicants from African and Asian countries who demonstrate commitment to humanitarian and development work. Scholarship decisions are final and determined solely by GIHDS. Providing false or misleading information in a scholarship application may result in withdrawal of the offer and dismissal from the program without refund.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">6. Learner Responsibilities</h2>
            <p className="text-slate-600 leading-relaxed mb-4">As a learner, you agree to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Complete all coursework, assignments, and program requirements in a timely manner</li>
              <li>Maintain academic integrity and refrain from plagiarism, collusion, or misuse of AI tools</li>
              <li>Respect fellow learners, faculty, and staff in all interactions</li>
              <li>Keep your login credentials, course materials, and prompt-bank content confidential and non-transferable</li>
              <li>Comply with all program rules, LMS usage guidelines, and applicable laws</li>
            </ul>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">7. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              All course materials, curricula, prompt banks, videos, assessments, and other content provided by GIHDS are the intellectual property of GIHDS (or its licensors) and are protected by copyright and other applicable laws. Learners may use these materials for personal educational purposes only. Reproduction, redistribution, resale, commercial use, or publication of GIHDS materials—in whole or in part—is strictly prohibited.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">8. Academic Documents Upon Completion</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Upon successful completion of program requirements and fulfilment of all academic and financial obligations, eligible graduates receive the corresponding academic document as described on each program page: a Master's Degree Certificate and Academic Transcript for the Master of Humanitarian Action & Development and the Master of Development Project & Program Management; a Doctoral Degree Certificate and Academic Transcript for the Doctor of Humanitarian Action & Development; and a Certificate of Completion for the AI in Humanitarian Action & Development short course.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              To the fullest extent permitted by law, GIHDS shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your access to or use of our website, services, or programs. Total aggregate liability shall not exceed the tuition amount paid by the learner for the specific program in question.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">10. Modifications</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              GIHDS may update these Terms of Use from time to time. Material changes will be posted on this page with a revised "Last updated" date. Continued use of the website or services after such changes constitutes acceptance of the updated terms.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">11. Contact</h2>
            <p className="text-slate-600 leading-relaxed mb-6">For any questions regarding these Terms of Use, please contact us:</p>
            <div className="bg-[#F1F5F8] p-6 rounded-sm">
              <p className="text-slate-900 font-semibold">Geneva Institute of Humanitarian & Development Studies (GIHDS)</p>
              <p className="text-slate-600">Email: admissions@gihds.org</p>
              <p className="text-slate-600">Website: www.gihds.org</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
